import React, { useRef, useState, useEffect } from "react";
import { auth, db } from "../../firebase";
import { signOut, updatePassword } from "firebase/auth";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import {
  clearStoredLoginOtpChallenge,
  getStoredLoginOtpChallenge,
  createStoredLoginOtpChallenge,
  refreshStoredLoginOtpChallenge,
  verifyStoredLoginOtp,
} from "../../services/authFlowService";
import { createOtpRequestId, generateOtpCode, sendOtpEmail, sendOtpSms, OTP_EXPIRATION_MS, sendPasswordChangeNotification } from "../../services/otpService";
import "./otpmodalscss/StaffChangeVerificationModal.css";

const StaffChangeVerificationModal = ({ 
  isOpen, 
  onClose,
  flow = "password-change",
  pendingContactChange = null,
  pendingPasswordChange = null,
  onSuccess = null,
  onCancel = null
}) => {
  const isContactChange = flow === "contact-change";
  
  const OTP_LENGTH = 6;
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [otpExpired, setOtpExpired] = useState(false);
  const [resendText, setResendText] = useState("Resend");
  const [isResending, setIsResending] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const inputsRef = useRef([]);
  const [step, setStep] = useState(1);
  const [verificationMethod, setVerificationMethod] = useState("sms");
  const [hasSmsBeenSent, setHasSmsBeenSent] = useState(false);
  const [hasEmailBeenSent, setHasEmailBeenSent] = useState(false);
  const [timerActive, setTimerActive] = useState(() => {
    return flow === "password-change";
  });
  
  // Enhanced step state for email/contact changes (matching UserChangeOtpModal)
  const [profileChangeStep, setProfileChangeStep] = useState("oldEmail");
  const [newEmailOtpChallenge, setNewEmailOtpChallenge] = useState(null);
  const [newContactSmsOtpChallenge, setNewContactSmsOtpChallenge] = useState(null);
  // Password change specific state
  const [passwordVerificationMethod, setPasswordVerificationMethod] = useState("email");
  const [passwordSmsOtpChallenge, setPasswordSmsOtpChallenge] = useState(null);
  const [passwordHasSmsBeenSent, setPasswordHasSmsBeenSent] = useState(false);
  const [passwordTimerActive, setPasswordTimerActive] = useState(false);
  const [userData, setUserData] = useState(null);
  const [oldOtpChallenge, setOldOtpChallenge] = useState(null);
  const [oldSmsOtpChallenge, setOldSmsOtpChallenge] = useState(null);
  const [newSmsOtpChallenge, setNewSmsOtpChallenge] = useState(null);
  const [oldContactOtpChallenge, setOldContactOtpChallenge] = useState(null);
  const [oldContactSmsOtpChallenge, setOldContactSmsOtpChallenge] = useState(null);
  const otpSentRef = useRef(false);
  
  // Get user data from Firebase
  useEffect(() => {
    const fetchUserData = async () => {
      const user = auth.currentUser;
      if (user) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }
      }
    };
    fetchUserData();
  }, []);
  
  const normalizePhone = (phone) => {
    if (!phone) return null;
    const cleaned = String(phone).replace(/\D/g, "");
    if (cleaned.length === 10 && cleaned.startsWith("9")) {
      return `+63${cleaned}`;
    } else if (cleaned.length === 11 && cleaned.startsWith("0")) {
      return `+63${cleaned.slice(1)}`;
    } else if (cleaned.length === 12 && cleaned.startsWith("63")) {
      return `+${cleaned}`;
    } else if (phone.startsWith("+63")) {
      return phone;
    }
    return null;
  };
  
  const oldPhone = pendingContactChange?.currentPhone;
  const normalizedOldPhone = normalizePhone(oldPhone);
  const newPhone = pendingContactChange?.newPhone;
  const normalizedNewPhone = normalizePhone(newPhone);
  
  // Email change data
  const oldEmail = pendingContactChange?.oldEmail || userData?.email || "";
  const newEmail = pendingContactChange?.newEmail || "";
  const hasEmailChange = Boolean(oldEmail && newEmail && oldEmail.trim().toLowerCase() !== newEmail.trim().toLowerCase());
  const hasContactChange = Boolean(normalizedNewPhone && normalizedOldPhone && normalizedNewPhone !== normalizedOldPhone);
  
  // Get staff phone for password change
  const rawPhoneForPassword = userData?.phone || userData?.contact || (userData?.phoneLast10 ? `+63${userData.phoneLast10}` : null);
  const normalizedPhoneForPassword = normalizePhone(rawPhoneForPassword);
  
  const sendOtpToEmail = async (email, purpose, saveChallenge) => {
    console.log("sendOtpToEmail called:", { email, purpose });
    if (!email) {
      throw new Error("Unable to send OTP. Email is missing.");
    }

    const otpCode = generateOtpCode();
    const requestId = createOtpRequestId(purpose);
    const expiresAt = Date.now() + OTP_EXPIRATION_MS;

    console.log("sendOtpEmail about to send OTP:", { otpCode, requestId, expiresAt });
    await sendOtpEmail({
      email: email, // Use the actual email parameter
      otp: otpCode,
      purpose,
      requestId,
      expiresAt,
    });

    const challenge = { email, otp: otpCode, requestId, expiresAt };
    saveChallenge(challenge);
    setOtp(Array(OTP_LENGTH).fill(""));
    setOtpExpired(false);
    setTimeLeft(60);
    setTimerActive(true);
    setError("");
    return challenge;
  };
  
  const sendOtpToSms = async (phone, purpose, saveChallenge) => {
    console.log("sendOtpToSms called:", { phone, purpose });
    if (!phone) {
      throw new Error("Unable to send SMS OTP. Phone number is missing.");
    }

    const otpCode = generateOtpCode();
    const requestId = createOtpRequestId(purpose);
    const expiresAt = Date.now() + OTP_EXPIRATION_MS;

    console.log("sendOtpSms about to send OTP:", { otpCode, requestId, expiresAt });
    await sendOtpSms({
      phone,
      otp: otpCode,
      purpose,
      requestId,
      expiresAt,
    });

    const challenge = { phone, otp: otpCode, requestId, expiresAt };
    saveChallenge(challenge);
    setOtp(Array(OTP_LENGTH).fill(""));
    setOtpExpired(false);
    setTimeLeft(60);
    setTimerActive(true);
    setError("");
    return challenge;
  };
  
  // Initialize contact change flow
  useEffect(() => {
    if (!isOpen || !isContactChange || !pendingContactChange) {
      return;
    }

    if (otpSentRef.current) {
      return;
    }

    const initFlow = async () => {
      otpSentRef.current = true;

      // Determine initial step based on what's being changed
      if (hasEmailChange && hasContactChange) {
        // Combined change: start with email verification — auto-send OTP to old email
        setProfileChangeStep("oldEmail");
        setVerificationMethod("email");
        setHasEmailBeenSent(false);
        setTimerActive(false);
        try {
          await sendOtpToEmail(oldEmail, "staff-contact-change-old-email", setOldOtpChallenge);
          setHasEmailBeenSent(true);
          setTimerActive(true);
        } catch (err) {
          console.error("Failed to send OTP to old email:", err);
        }
      } else if (hasEmailChange) {
        // Email only: start with old email verification — auto-send OTP to old email
        setProfileChangeStep("oldEmail");
        setVerificationMethod("email");
        setHasEmailBeenSent(false);
        setTimerActive(false);
        try {
          await sendOtpToEmail(oldEmail, "staff-contact-change-old-email", setOldOtpChallenge);
          setHasEmailBeenSent(true);
          setTimerActive(true);
        } catch (err) {
          console.error("Failed to send OTP to old email:", err);
        }
      } else if (hasContactChange) {
        // Contact only: start with old contact verification
        setProfileChangeStep("oldContact");
        setVerificationMethod("sms");
        setHasSmsBeenSent(false);
      } else {
        // Fallback
        setVerificationMethod("sms");
        setHasSmsBeenSent(false);
      }
    };

    initFlow();
  }, [isOpen, isContactChange, pendingContactChange, normalizedOldPhone, hasEmailChange, hasContactChange, oldEmail]);
  
  // Initialize password change flow
  useEffect(() => {
    if (!isOpen || isContactChange || !pendingPasswordChange) {
      return;
    }
    
    if (otpSentRef.current) {
      return;
    }
    
    const initFlow = async () => {
      otpSentRef.current = true;

      // Check if OTP was already sent by StaffChangePassword
      const storedChallenge = getStoredLoginOtpChallenge();
      if (storedChallenge && storedChallenge.otp) {
        // OTP was already sent, use it immediately
        setOldOtpChallenge(storedChallenge);
        setHasEmailBeenSent(true);
        setPasswordTimerActive(true);
        setPasswordVerificationMethod("email");
      } else {
        // No pre-sent OTP — wait for user to click Send Email
        setHasEmailBeenSent(false);
        setPasswordTimerActive(false);
      }
    };
    
    initFlow();
  }, [isOpen, isContactChange, pendingPasswordChange]);
  
  useEffect(() => {
    if (!isOpen) return;

    const activeTimer = isContactChange
      ? (profileChangeStep === "newEmail" || profileChangeStep === "newContact" ? passwordTimerActive : timerActive)
      : passwordTimerActive;

    if (!activeTimer || timeLeft <= 0) {
      if (timeLeft <= 0) {
        setOtpExpired(true);
      }
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          setOtpExpired(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, timeLeft, timerActive, passwordTimerActive, isContactChange, profileChangeStep]);

  const formatTime = (sec) => {
    const minutes = Math.floor(sec / 60);
    const seconds = sec % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const isOtpComplete = otp.every((digit) => digit !== "");

  const handleChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError("");

    if (value && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputsRef.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    const pasteData = e.clipboardData.getData("text").trim().slice(0, OTP_LENGTH);
    if (/^\d+$/.test(pasteData)) {
      const pasteArray = pasteData.split("");
      const newOtp = Array(OTP_LENGTH).fill("");
      pasteArray.forEach((char, i) => {
        if (i < OTP_LENGTH) newOtp[i] = char;
      });
      setOtp(newOtp);
      setError("");
      const lastFilled = pasteArray.length - 1;
      inputsRef.current[Math.min(lastFilled, OTP_LENGTH - 1)].focus();
    }
    e.preventDefault();
  };
  
  const handleTryAnotherWay = () => {
    const newMethod = verificationMethod === "sms" ? "email" : "sms";
    setVerificationMethod(newMethod);
    if (newMethod === "sms") {
      setHasSmsBeenSent(false);
    } else {
      setHasEmailBeenSent(false);
    }
    setOtp(Array(OTP_LENGTH).fill(""));
    setTimeLeft(60);
    setOtpExpired(false);
    setTimerActive(false);
    setError("");
  };

  const handlePasswordTryAnotherWay = () => {
    const newMethod = passwordVerificationMethod === "sms" ? "email" : "sms";
    setPasswordVerificationMethod(newMethod);
    setOtp(Array(OTP_LENGTH).fill(""));
    setTimeLeft(60);
    setOtpExpired(false);
    setPasswordTimerActive(false);
    setError("");

    if (newMethod === "sms") {
      // User will click "Send SMS" button
      setPasswordHasSmsBeenSent(false);
    } else {
      // Switch back to email — user will click "Send Email" button
      setHasEmailBeenSent(false);
    }
  };
  
  const handleSendPasswordSms = async () => {
    if (!normalizedPhoneForPassword) {
      setError("No registered phone number found.");
      return;
    }
    setIsSendingOtp(true);
    try {
      await sendOtpToSms(normalizedPhoneForPassword, "password-change", setPasswordSmsOtpChallenge);
      setPasswordHasSmsBeenSent(true);
      setPasswordTimerActive(true);
      setOtp(Array(OTP_LENGTH).fill(""));
      setTimeLeft(60);
      setOtpExpired(false);
    } catch (err) {
      console.error("Failed to send password change SMS OTP:", err);
      setError(err.message || "Failed to send verification code.");
    } finally {
      setIsSendingOtp(false);
    }
  };
  
  const handleSendOldSms = async () => {
    if (!normalizedOldPhone) {
      setError("No registered phone number found.");
      return;
    }
    setIsSendingOtp(true);
    try {
      await sendOtpToSms(normalizedOldPhone, "staff-contact-change-old-phone", setOldSmsOtpChallenge);
      setHasSmsBeenSent(true);
    } catch (err) {
      console.error("Failed to send SMS OTP:", err);
      setError(err.message || "Failed to send verification code.");
    } finally {
      setIsSendingOtp(false);
    }
  };
  
  const handleSendOldEmail = async () => {
    const email = oldEmail || userData?.email;
    console.log("handleSendOldEmail called with email:", email);
    setIsSendingOtp(true);
    try {
      await sendOtpToEmail(email, "staff-contact-change-old-email", setOldOtpChallenge);
      setHasEmailBeenSent(true);
    } catch (err) {
      console.error("Failed to send email OTP:", err);
      setError(err.message || "Failed to send verification code.");
    } finally {
      setIsSendingOtp(false);
    }
  };
  
  const handleSendNewPhoneSms = async () => {
    if (!normalizedNewPhone) {
      setError("No new phone number provided.");
      return;
    }
    setIsSendingOtp(true);
    try {
      await sendOtpToSms(normalizedNewPhone, "staff-contact-change-new-phone", setNewSmsOtpChallenge);
      setHasSmsBeenSent(true);
    } catch (err) {
      console.error("Failed to send SMS OTP for new phone:", err);
      setError(err.message || "Failed to send verification code.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleSendNewContactSms = async () => {
    if (!normalizedNewPhone) {
      setError("No new phone number provided.");
      return;
    }
    setIsSendingOtp(true);
    try {
      await sendOtpToSms(normalizedNewPhone, "staff-contact-change-new-phone", setNewContactSmsOtpChallenge);
      setHasSmsBeenSent(true);
      setPasswordTimerActive(true);
      setOtp(Array(OTP_LENGTH).fill(""));
      setTimeLeft(60);
      setOtpExpired(false);
    } catch (err) {
      console.error("Failed to send SMS OTP for new contact:", err);
      setError(err.message || "Failed to send verification code.");
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerify = async () => {
    if (!isOtpComplete) {
      setError("Please enter all 6 digits");
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
      return;
    }

    if (otpExpired) {
      setError("Code has expired. Please request a new code.");
      setOtp(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
      return;
    }

    setIsVerifying(true);

    try {
      const enteredOtp = otp.join("");
      
      if (isContactChange) {
        // Handle profile change steps (email/contact)
        
        // Step 1: Verify old email (email change) or old contact (contact change)
        if (profileChangeStep === "oldEmail") {
          const challenge = verificationMethod === 'sms' ? oldSmsOtpChallenge : oldOtpChallenge;

          if (!challenge || Date.now() > challenge.expiresAt) {
            throw new Error("OTP expired or invalid. Please request a new code.");
          }

          if (enteredOtp !== challenge.otp) {
            throw new Error("Invalid OTP. Please try again.");
          }

          // If email change, move to step 2 (verify new email)
          if (hasEmailChange) {
            setProfileChangeStep("newEmail");
            setOtp(Array(OTP_LENGTH).fill(""));
            setTimeLeft(60);
            setOtpExpired(false);
            setError("");
            setVerificationMethod("email");
            setHasEmailBeenSent(false);
            setTimerActive(false);
            setPasswordTimerActive(true);

            // Send OTP to new email
            const otpCode = generateOtpCode();
            const requestId = createOtpRequestId("profile-change");
            const expiresAt = Date.now() + OTP_EXPIRATION_MS;

            await sendOtpEmail({
              email: newEmail,
              otp: otpCode,
              purpose: 'profile-change',
              requestId,
              expiresAt,
            });

            setNewEmailOtpChallenge({ email: newEmail, otp: otpCode, requestId, expiresAt });
            setHasEmailBeenSent(true);
            setPasswordTimerActive(true);
            return;
          }

          // If only contact change, move to step 2 (verify new contact)
          if (hasContactChange && !hasEmailChange) {
            setProfileChangeStep("newContact");
            setOtp(Array(OTP_LENGTH).fill(""));
            setTimeLeft(60);
            setOtpExpired(false);
            setError("");
            setVerificationMethod("sms");
            setHasSmsBeenSent(false);
            setTimerActive(false);
            setPasswordTimerActive(false);
            return;
          }
        }

        // Step 1: Verify old contact (contact change) via SMS or email
        if (profileChangeStep === "oldContact") {
          const challenge = verificationMethod === 'sms' ? oldSmsOtpChallenge : oldOtpChallenge;

          if (!challenge || Date.now() > challenge.expiresAt) {
            throw new Error("OTP expired or invalid. Please request a new code.");
          }

          if (enteredOtp !== challenge.otp) {
            throw new Error("Invalid OTP. Please try again.");
          }

          // Move to step 2 (verify new contact)
          setProfileChangeStep("newContact");
          setOtp(Array(OTP_LENGTH).fill(""));
          setTimeLeft(60);
          setOtpExpired(false);
          setError("");
          setVerificationMethod("sms");
          setHasSmsBeenSent(false);
          setTimerActive(false);
          setPasswordTimerActive(false);
          return;
        }
        
        // Step 2: Verify new email (email change)
        if (profileChangeStep === "newEmail") {
          const challenge = newEmailOtpChallenge;
          
          if (!challenge || Date.now() > challenge.expiresAt) {
            throw new Error("OTP expired or invalid. Please request a new code.");
          }
          
          if (enteredOtp !== challenge.otp) {
            throw new Error("Invalid OTP. Please try again.");
          }
          
          // If also changing contact, move to step 3 (verify new contact)
          if (hasContactChange) {
            setProfileChangeStep("newContact");
            setOtp(Array(OTP_LENGTH).fill(""));
            setTimeLeft(60);
            setOtpExpired(false);
            setError("");
            setVerificationMethod("sms");
            setHasSmsBeenSent(false);
            setTimerActive(false);
            setPasswordTimerActive(false);
            return;
          }
          
          // Email change complete - update Firestore
          const uid = auth.currentUser?.uid || pendingContactChange?.uid;
          if (uid) {
            const updatePayload = {
              email: newEmail,
              emailLower: newEmail.toLowerCase(),
              language: pendingContactChange?.language,
              updatedAt: serverTimestamp()
            };
            
            await updateDoc(doc(db, 'users', uid), updatePayload);
            
            if (onSuccess) {
              onSuccess({ type: 'email' });
            }
            handleClose();
          }
          return;
        }
        
        // Step 2 or 3: Verify new contact (contact change or combined change)
        if (profileChangeStep === "newContact") {
          const challenge = newContactSmsOtpChallenge;
          
          if (!challenge || Date.now() > challenge.expiresAt) {
            throw new Error("OTP expired or invalid. Please request a new code.");
          }
          
          if (enteredOtp !== challenge.otp) {
            throw new Error("Invalid OTP. Please try again.");
          }
          
          // Contact change complete - update Firestore
          const uid = auth.currentUser?.uid || pendingContactChange?.uid;
          if (uid) {
            // Extract last 10 digits from normalizedNewPhone (it may already have +63 prefix)
            const phoneLast10Digits = String(normalizedNewPhone).replace(/\D/g, "").slice(-10);
            const updatePayload = {
              phone: `+63${phoneLast10Digits}`,
              phoneLast10: phoneLast10Digits,
              language: pendingContactChange?.language,
              updatedAt: serverTimestamp()
            };

            // If email was also changed, include it
            if (hasEmailChange && newEmail) {
              updatePayload.email = newEmail;
              updatePayload.emailLower = newEmail.toLowerCase();
            }

            await updateDoc(doc(db, 'users', uid), updatePayload);
            
            if (onSuccess) {
              onSuccess({ type: hasEmailChange ? 'both' : 'contact' });
            }
            handleClose();
          }
          return;
        }
      } else {
        // Password change flow
        let isValid = false;
        if (passwordVerificationMethod === "email") {
          // Check oldOtpChallenge first (set from StaffChangePassword)
          if (oldOtpChallenge && oldOtpChallenge.otp === enteredOtp && Date.now() < oldOtpChallenge.expiresAt) {
            isValid = true;
          } else {
            // Try using verifyStoredLoginOtp as fallback
            try {
              const result = verifyStoredLoginOtp(enteredOtp);
              isValid = result.success;
            } catch (err) {
              console.error("Verification error:", err);
            }
          }
        } else if (passwordVerificationMethod === "sms") {
          if (passwordSmsOtpChallenge && passwordSmsOtpChallenge.otp === enteredOtp && Date.now() < passwordSmsOtpChallenge.expiresAt) {
            isValid = true;
          }
        }
        
        if (isValid) {
          if (pendingPasswordChange?.newPassword && pendingPasswordChange?.email) {
            let authUser = auth.currentUser;
            
            if (!authUser || authUser.email !== pendingPasswordChange.email) {
              const { signInWithEmailAndPassword } = await import("firebase/auth");
              await signInWithEmailAndPassword(
                auth,
                pendingPasswordChange.email,
                pendingPasswordChange.currentPassword
              );
              authUser = auth.currentUser;
            }
            
            if (authUser) {
              await updatePassword(authUser, pendingPasswordChange.newPassword);
              
              await updateDoc(doc(db, 'users', authUser.uid), {
                passwordChanged: true,
                updatedAt: serverTimestamp()
              });
              
              // Send password change notification email to the staff's real email
              await sendPasswordChangeNotification({ email: pendingPasswordChange.email });
              
              localStorage.removeItem('pendingPasswordChange');
              clearStoredLoginOtpChallenge();
              
              await signOut(auth);
              
              if (onSuccess) {
                onSuccess({ passwordChanged: true });
              }
              handleClose();
            }
          }
        } else {
          setError("Invalid verification code. Please try again.");
          setOtp(Array(OTP_LENGTH).fill(""));
          inputsRef.current[0]?.focus();
        }
      }
    } catch (err) {
      console.error("Verification Error:", err);
      setError(err.message || "Unable to continue. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resendText === "Sent!" || isResending || isSendingOtp) return;

    try {
      setIsResending(true);
      setIsVerifying(false);
      setError("");

      if (isContactChange) {
        if (step === 1) {
          if (verificationMethod === "sms" && normalizedOldPhone) {
            await sendOtpToSms(normalizedOldPhone, "staff-contact-change-old-phone", setOldSmsOtpChallenge);
          } else if (verificationMethod === "email") {
            const email = oldEmail || userData?.email;
            await sendOtpToEmail(email, "staff-contact-change-old-email", setOldOtpChallenge);
          }
        } else if (step === 2 && normalizedNewPhone) {
          await sendOtpToSms(normalizedNewPhone, "staff-contact-change-new-phone", setNewSmsOtpChallenge);
        }
      } else {
        // For password change, handle both sms and email
        if (passwordVerificationMethod === "sms") {
          // Send password change SMS
          try {
            await sendOtpToSms(normalizedPhoneForPassword, "password-change", setPasswordSmsOtpChallenge);
          } catch (err) {
            console.error("Resend password change SMS error:", err);
            throw err;
          }
        } else {
          // For password change email
          let refreshedChallenge;
          try {
            refreshedChallenge = refreshStoredLoginOtpChallenge();
          } catch (refreshErr) {
            // If refresh fails, create a new challenge
            const email = pendingPasswordChange?.email || userData?.email;
            refreshedChallenge = createStoredLoginOtpChallenge({
              email,
              role: 'staff',
              roles: ['staff'],
              route: 'password-change',
              keepSignedIn: false
            });
          }
        
          const email = pendingPasswordChange?.email || userData?.email;
          await sendOtpEmail({
            email: email,
            otp: refreshedChallenge.otp,
            purpose: 'password-change',
            requestId: refreshedChallenge.requestId,
            expiresAt: refreshedChallenge.expiresAt,
          });
        
          setOldOtpChallenge(refreshedChallenge);
        }
      }

      setResendText("Sent!");
      setOtp(Array(OTP_LENGTH).fill(""));
      setOtpExpired(false);
      setTimeLeft(60);
      if (!isContactChange) {
        setPasswordTimerActive(true);
      } else {
        setTimerActive(true);
      }
      inputsRef.current[0]?.focus();
      setTimeout(() => setResendText("Resend"), 2000);
    } catch (err) {
      console.error("Resend Error:", err);
      setError(err.message);
    } finally {
      setIsResending(false);
    }
  };

  const handleClose = () => {
    if (isContactChange) {
      localStorage.removeItem('pendingStaffContactChange');
    } else {
      localStorage.removeItem('pendingPasswordChange');
    }

    // Reset all state
    setOtp(Array(OTP_LENGTH).fill(""));
    setError("");
    setStep(1);
    setProfileChangeStep("oldEmail");
    setVerificationMethod("sms");
    setHasSmsBeenSent(false);
    setHasEmailBeenSent(false);
    setTimerActive(false);
    setPasswordVerificationMethod("email");
    setPasswordSmsOtpChallenge(null);
    setPasswordHasSmsBeenSent(false);
    setPasswordTimerActive(false);
    setOldOtpChallenge(null);
    setOldSmsOtpChallenge(null);
    setNewSmsOtpChallenge(null);
    setNewEmailOtpChallenge(null);
    setNewContactSmsOtpChallenge(null);
    setOtpExpired(false);
    setTimeLeft(60);
    otpSentRef.current = false;

    onClose();
  };
  
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    handleClose();
  };
  
  const getDisplayTarget = () => {
    if (isContactChange) {
      if (profileChangeStep === "oldEmail") {
        if (verificationMethod === "sms") {
          return normalizedOldPhone || "your phone number";
        } else {
          return oldEmail || userData?.email || "your email";
        }
      } else if (profileChangeStep === "newEmail") {
        return newEmail || "your new email";
      } else if (profileChangeStep === "oldContact") {
        if (verificationMethod === "sms") {
          return normalizedOldPhone || "your phone number";
        } else {
          return oldEmail || userData?.email || "your email";
        }
      } else if (profileChangeStep === "newContact") {
        return normalizedNewPhone || "your new phone number";
      }
    } else {
      if (passwordVerificationMethod === "sms") {
        return normalizedPhoneForPassword || "your phone number";
      } else {
        return userData?.email || "your email";
      }
    }
  };

  const getDisplayTitle = () => {
    if (isContactChange) {
      if (profileChangeStep === "oldEmail") {
        return "Verify It's You";
      } else if (profileChangeStep === "newEmail") {
        return "Verify Your New Email";
      } else if (profileChangeStep === "oldContact") {
        return "Verify It's You";
      } else if (profileChangeStep === "newContact") {
        return "Verify Your New Phone";
      }
    }
    return "Enter Verification Code";
  };

  const getDisplaySubtitle = () => {
    if (isContactChange) {
      if (profileChangeStep === "oldEmail") {
        return `Enter 6-digit code we sent to your ${verificationMethod === "sms" ? "phone" : "email"}: `;
      } else if (profileChangeStep === "newEmail") {
        return `Enter 6-digit code we sent to your email: `;
      } else if (profileChangeStep === "oldContact") {
        return `Enter 6-digit code we sent to your ${verificationMethod === "sms" ? "phone" : "email"}: `;
      } else if (profileChangeStep === "newContact") {
        return `Enter 6-digit code we sent to your phone: `;
      }
    }
    return `Enter 6-digit code we sent to your ${passwordVerificationMethod === "sms" ? "phone" : "email"}: `;
  };
  
  const showTryAnotherWay =
    (isContactChange && (
      (profileChangeStep === "oldEmail" && (
        (verificationMethod === "email" && normalizedOldPhone) ||
        (verificationMethod === "sms" && (oldEmail || userData?.email))
      )) ||
      (profileChangeStep === "oldContact" && (
        (verificationMethod === "email" && normalizedOldPhone) ||
        (verificationMethod === "sms" && (oldEmail || userData?.email))
      ))
    )) ||
    (!isContactChange && normalizedPhoneForPassword);

  const showBackButton = false;

  // Auto-focus first input when modal opens
  useEffect(() => {
    if (isOpen) {
      inputsRef.current[0]?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="staff-change-verification-modal-overlay">
      <div className="staff-change-verification-modal-card">
        <h1 className="staff-change-verification-title">
          {getDisplayTitle()}
        </h1>
        <p className="staff-change-verification-subtitle">
          {getDisplaySubtitle()}
          <strong>{getDisplayTarget()}</strong>
        </p>

        <div className="staff-change-verification-otp-inputs">
          {otp.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (inputsRef.current[index] = el)}
              type="text"
              maxLength="1"
              value={digit}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              className="staff-change-verification-otp-input"
              disabled={isVerifying}
              autoFocus={index === 0}
            />
          ))}
        </div>

        {error && <div className="staff-change-verification-error-text">{error}</div>}

        {otpExpired && (
          <div className="staff-change-verification-error-text">Code has expired. Please request a new one.</div>
        )}

        {((isContactChange && timerActive) || (!isContactChange && passwordTimerActive) || (isContactChange && (profileChangeStep === "newEmail" || profileChangeStep === "newContact") && passwordTimerActive)) && (
          <p className="staff-change-verification-timer-section">
            Code expires in{" "}
            <span className={`staff-change-verification-timer ${otpExpired ? "expired" : ""}`}>
              {formatTime(timeLeft)}
            </span>
          </p>
        )}

        {/* Resend/Send SMS/Email section */}
        {isContactChange ? (
          // newContact step - send SMS to new contact
          profileChangeStep === "newContact" ? (
            <p className="staff-change-verification-resend-section">
              {hasSmsBeenSent ? (
                <>Didn't receive a code?{" "}
                <span
                  className={`staff-change-verification-resend-link ${(resendText === "Sent!" || isSendingOtp) ? "disabled" : ""}`}
                  onClick={async () => {
                    setIsSendingOtp(true);
                    try {
                      await sendOtpToSms(normalizedNewPhone, "staff-contact-change-new-phone", setNewContactSmsOtpChallenge);
                      setHasSmsBeenSent(true);
                      setOtp(Array(OTP_LENGTH).fill(""));
                      setTimeLeft(60);
                      setOtpExpired(false);
                      setPasswordTimerActive(true);
                    } catch (err) {
                      console.error("Failed to send SMS OTP for new contact:", err);
                      setError(err.message || "Failed to send verification code.");
                    } finally {
                      setIsSendingOtp(false);
                    }
                  }}
                  style={{ pointerEvents: (resendText === "Sent!" || isSendingOtp) ? "none" : "auto", opacity: (resendText === "Sent!" || isSendingOtp) ? 0.5 : 1 }}
                >
                  {isSendingOtp ? "Sending..." : resendText}
                </span></>
              ) : (
                <button
                  className="staff-change-verification-send-btn"
                  onClick={async () => {
                    setIsSendingOtp(true);
                    try {
                      await sendOtpToSms(normalizedNewPhone, "staff-contact-change-new-phone", setNewContactSmsOtpChallenge);
                      setHasSmsBeenSent(true);
                      setOtp(Array(OTP_LENGTH).fill(""));
                      setTimeLeft(60);
                      setOtpExpired(false);
                      setPasswordTimerActive(true);
                    } catch (err) {
                      console.error("Failed to send SMS OTP for new contact:", err);
                      setError(err.message || "Failed to send verification code.");
                    } finally {
                      setIsSendingOtp(false);
                    }
                  }}
                  disabled={isSendingOtp}
                >
                  {isSendingOtp ? "Sending..." : "Send SMS"}
                </button>
              )}
            </p>
          ) : // newEmail step - email already sent in handleVerify
          profileChangeStep === "newEmail" ? (
            <p className="staff-change-verification-resend-section">
              {hasEmailBeenSent ? (
                <>Didn't receive a code?{" "}
                <span
                  className={`staff-change-verification-resend-link ${resendText === "Sent!" || isResending || isSendingOtp ? "disabled" : ""}`}
                  onClick={async () => {
                    setIsResending(true);
                    setIsVerifying(false);
                    setError("");
                    try {
                      const otpCode = generateOtpCode();
                      const requestId = createOtpRequestId("profile-change");
                      const expiresAt = Date.now() + OTP_EXPIRATION_MS;

                      await sendOtpEmail({
                        email: newEmail,
                        otp: otpCode,
                        purpose: 'profile-change',
                        requestId,
                        expiresAt,
                      });

                      setNewEmailOtpChallenge({ email: newEmail, otp: otpCode, requestId, expiresAt });
                      setResendText("Sent!");
                      setOtp(Array(OTP_LENGTH).fill(""));
                      setOtpExpired(false);
                      setTimeLeft(60);
                      setPasswordTimerActive(true);
                      inputsRef.current[0]?.focus();
                      setTimeout(() => setResendText("Resend"), 2000);
                    } catch (err) {
                      console.error("Resend email OTP error:", err);
                      setError(err.message);
                    } finally {
                      setIsResending(false);
                    }
                  }}
                  style={{
                    cursor: resendText === "Sent!" || isResending || isSendingOtp ? "default" : "pointer",
                  }}
                >
                  {isResending || isSendingOtp ? "Sending..." : resendText}
                </span></>
              ) : null}
            </p>
          ) : // oldContact step - send SMS to old contact
          profileChangeStep === "oldContact" ? (
            verificationMethod === "sms" ? (
              <>
                {hasSmsBeenSent ? (
                  <p className="staff-change-verification-resend-section">
                    <>Didn't receive a code?{" "}
                    <span
                      className={`staff-change-verification-resend-link ${(resendText === "Sent!" || isSendingOtp) ? "disabled" : ""}`}
                      onClick={handleSendOldSms}
                      style={{ pointerEvents: (resendText === "Sent!" || isSendingOtp) ? "none" : "auto", opacity: (resendText === "Sent!" || isSendingOtp) ? 0.5 : 1 }}
                    >
                      {isSendingOtp ? "Sending..." : resendText}
                    </span></>
                  </p>
                ) : (
                  <button
                    className="staff-change-verification-send-btn"
                    onClick={handleSendOldSms}
                    disabled={isSendingOtp}
                  >
                    {isSendingOtp ? "Sending..." : "Send SMS"}
                  </button>
                )}
              </>
            ) : (
              <>
                {hasEmailBeenSent ? (
                  <p className="staff-change-verification-resend-section">
                    <>Didn't receive a code?{" "}
                    <span
                      className={`staff-change-verification-resend-link ${(resendText === "Sent!" || isSendingOtp) ? "disabled" : ""}`}
                      onClick={handleSendOldEmail}
                      style={{ pointerEvents: (resendText === "Sent!" || isSendingOtp) ? "none" : "auto", opacity: (resendText === "Sent!" || isSendingOtp) ? 0.5 : 1 }}
                    >
                      {isSendingOtp ? "Sending..." : resendText}
                    </span></>
                  </p>
                ) : (
                  <button
                    className="staff-change-verification-send-btn"
                    onClick={handleSendOldEmail}
                    disabled={isSendingOtp}
                  >
                    {isSendingOtp ? "Sending..." : "Send Email"}
                  </button>
                )}
              </>
            )
          ) : // oldEmail step - send email to old email
          profileChangeStep === "oldEmail" ? (
            verificationMethod === "sms" ? (
              <>
                {hasSmsBeenSent ? (
                  <p className="staff-change-verification-resend-section">
                    <>Didn't receive a code?{" "}
                    <span
                      className={`staff-change-verification-resend-link ${(resendText === "Sent!" || isSendingOtp) ? "disabled" : ""}`}
                      onClick={handleSendOldSms}
                      style={{ pointerEvents: (resendText === "Sent!" || isSendingOtp) ? "none" : "auto", opacity: (resendText === "Sent!" || isSendingOtp) ? 0.5 : 1 }}
                    >
                      {isSendingOtp ? "Sending..." : resendText}
                    </span></>
                  </p>
                ) : (
                  <button
                    className="staff-change-verification-send-btn"
                    onClick={handleSendOldSms}
                    disabled={isSendingOtp}
                  >
                    {isSendingOtp ? "Sending..." : "Send SMS"}
                  </button>
                )}
              </>
            ) : (
              <>
                {hasEmailBeenSent ? (
                  <p className="staff-change-verification-resend-section">
                    <>Didn't receive a code?{" "}
                    <span
                      className={`staff-change-verification-resend-link ${(resendText === "Sent!" || isSendingOtp) ? "disabled" : ""}`}
                      onClick={handleSendOldEmail}
                      style={{ pointerEvents: (resendText === "Sent!" || isSendingOtp) ? "none" : "auto", opacity: (resendText === "Sent!" || isSendingOtp) ? 0.5 : 1 }}
                    >
                      {isSendingOtp ? "Sending..." : resendText}
                    </span></>
                  </p>
                ) : (
                  <button
                    className="staff-change-verification-send-btn"
                    onClick={handleSendOldEmail}
                    disabled={isSendingOtp}
                  >
                    {isSendingOtp ? "Sending..." : "Send Email"}
                  </button>
                )}
              </>
            )
          ) : null
        ) : (
          <>
            {passwordVerificationMethod === "sms" ? (
              <>
                {passwordHasSmsBeenSent ? (
                  <p className="staff-change-verification-resend-section">
                    <>Didn't receive a code?{" "}
                    <span 
                      className={`staff-change-verification-resend-link ${(resendText === "Sent!" || isSendingOtp) ? "disabled" : ""}`} 
                      onClick={handleResend} 
                      style={{ pointerEvents: (resendText === "Sent!" || isSendingOtp) ? "none" : "auto", opacity: (resendText === "Sent!" || isSendingOtp) ? 0.5 : 1 }}
                    >
                      {isSendingOtp ? "Sending..." : resendText}
                    </span></>
                  </p>
                ) : (
                  <button
                    className="staff-change-verification-send-btn"
                    onClick={handleSendPasswordSms}
                    disabled={isSendingOtp}
                  >
                    {isSendingOtp ? "Sending..." : "Send SMS"}
                  </button>
                )}
              </>
            ) : (
              <>
                {hasEmailBeenSent ? (
                  <p className="staff-change-verification-resend-section">
                    <>Didn't receive a code?{" "}
                    <span
                      className={`staff-change-verification-resend-link ${resendText === "Sent!" || isResending || isSendingOtp ? "disabled" : ""}`}
                      onClick={handleResend}
                      style={{
                        cursor: resendText === "Sent!" || isResending || isSendingOtp ? "default" : "pointer",
                      }}
                    >
                      {isResending || isSendingOtp ? "Sending..." : resendText}
                    </span></>
                  </p>
                ) : (
                  <button
                    className="staff-change-verification-send-btn"
                    onClick={async () => {
                      setIsSendingOtp(true);
                      try {
                        const storedChallenge = getStoredLoginOtpChallenge();
                        if (storedChallenge && storedChallenge.otp) {
                          setOldOtpChallenge(storedChallenge);
                          setHasEmailBeenSent(true);
                          setPasswordTimerActive(true);
                          setOtp(Array(OTP_LENGTH).fill(""));
                          setTimeLeft(60);
                          setOtpExpired(false);
                          setError("");
                        } else {
                          const email = pendingPasswordChange?.email || userData?.email;
                          await sendOtpToEmail(email, "password-change", setOldOtpChallenge);
                          setHasEmailBeenSent(true);
                          setPasswordTimerActive(true);
                          setOtp(Array(OTP_LENGTH).fill(""));
                          setTimeLeft(60);
                          setOtpExpired(false);
                          setError("");
                        }
                      } catch (err) {
                        console.error("Failed to send password change email OTP:", err);
                        setError(err.message || "Failed to send verification code.");
                      } finally {
                        setIsSendingOtp(false);
                      }
                    }}
                    disabled={isSendingOtp}
                  >
                    {isSendingOtp ? "Sending..." : "Send Email"}
                  </button>
                )}
              </>
            )}
          </>
        )}

        {/* Back button for new email/contact steps */}
        {showBackButton && (
          <span
            className="staff-change-verification-back-link"
            onClick={() => {
              if (profileChangeStep === "newEmail") {
                setProfileChangeStep("oldEmail");
                setVerificationMethod("email");
                setHasEmailBeenSent(false);
                setOtp(Array(OTP_LENGTH).fill(""));
                setError("");
              } else if (profileChangeStep === "newContact") {
                if (hasEmailChange) {
                  setProfileChangeStep("newEmail");
                  setVerificationMethod("email");
                  setHasEmailBeenSent(true);
                  setOtp(Array(OTP_LENGTH).fill(""));
                  setError("");
                } else {
                  setProfileChangeStep("oldContact");
                  setVerificationMethod("sms");
                  setHasSmsBeenSent(false);
                  setOtp(Array(OTP_LENGTH).fill(""));
                  setError("");
                }
              }
            }}
          >
            ← Back
          </span>
        )}

        {/* Try Another Way button */}
        {showTryAnotherWay && !showBackButton && (
          <span
            className="staff-change-verification-back-link"
            onClick={isContactChange ? handleTryAnotherWay : handlePasswordTryAnotherWay}
          >
            Try another way
          </span>
        )}

        <button
          className="staff-change-verification-verify-btn"
          onClick={handleVerify}
          disabled={
            isVerifying || 
            !isOtpComplete || 
            otpExpired
          }
        >
          {isVerifying ? "Verifying..." : "Verify"}
        </button>

        <button 
          className="staff-change-verification-cancel-btn" 
          onClick={handleCancel}
          disabled={isVerifying}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default StaffChangeVerificationModal;
